import { type FileMessageContent, type GifAttachment } from "@fitness-app/data-models/entities/Chat";

/**
 * KLIPY GIF API client (TRE-2104). Docs: https://docs.klipy.com/gifs-api
 *
 * KLIPY's integration terms: requests originate from the end-user client (no proxying), media is
 * loaded straight from the URLs the API returns (no re-hosting), results are shown in the order
 * returned, and the UI carries KLIPY attribution. The app key is a publishable key, read from each
 * app's public env.
 */
export const KLIPY_API_URL = "https://api.klipy.com/api/v1";
export const KLIPY_ATTRIBUTION = "Powered by KLIPY";
export const KLIPY_PAGE_SIZE = 24;
/** Country code (ISO 3166-1 alpha-2) — KLIPY uses it to localise trending content. */
export const KLIPY_DEFAULT_LOCALE = "pl";

/** off | low | medium | high — trainer ↔ client chat, so keep the safe-search on. */
const CONTENT_FILTER = "medium";
/** webm dropped: nothing here plays it and it doubles the payload. */
const FORMAT_FILTER = "gif,webp,jpg,mp4";

type KlipyFormat = "gif" | "webp" | "jpg" | "mp4";
type KlipyRendition = "hd" | "md" | "sm" | "xs";

export interface KlipyFile {
  url: string;
  width: number;
  height: number;
  size: number;
}

export interface KlipyGif {
  id: number;
  slug: string;
  title: string;
  type: string;
  file: Partial<Record<KlipyRendition, Partial<Record<KlipyFormat, KlipyFile>>>>;
  blur_preview?: string;
}

interface KlipyPageResponse {
  result: boolean;
  data?: {
    data?: unknown[];
    current_page?: number;
    per_page?: number;
    has_next?: boolean;
  };
}

export type GifSearchSource = "trending" | "search";

export interface GifPage {
  gifs: KlipyGif[];
  page: number;
  hasNext: boolean;
}

export interface KlipyClientOptions {
  appKey: string;
  /** Stable, anonymous per-user id (we pass the auth user id) — drives KLIPY's personalisation. */
  customerId?: string | null;
  locale?: string;
  /** Injectable for tests. */
  fetchFn?: typeof fetch;
}

export interface GifPageRequest {
  query: string;
  page: number;
}

const isKlipyGif = (item: unknown): item is KlipyGif =>
  typeof item === "object" &&
  item !== null &&
  typeof (item as KlipyGif).slug === "string" &&
  typeof (item as KlipyGif).file === "object" &&
  (item as KlipyGif).file !== null;

export const buildGifPageUrl = (
  { appKey, customerId, locale = KLIPY_DEFAULT_LOCALE }: Omit<KlipyClientOptions, "fetchFn">,
  { query, page }: GifPageRequest,
): string => {
  const trimmed = query.trim();
  const endpoint = trimmed ? "search" : "trending";
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(KLIPY_PAGE_SIZE),
    locale,
    content_filter: CONTENT_FILTER,
    format_filter: FORMAT_FILTER,
  });
  if (trimmed) {
    params.set("q", trimmed);
  }
  if (customerId) {
    params.set("customer_id", customerId);
  }

  return `${KLIPY_API_URL}/${encodeURIComponent(appKey)}/gifs/${endpoint}?${params.toString()}`;
};

export const fetchGifPage = async (options: KlipyClientOptions, request: GifPageRequest): Promise<GifPage> => {
  const fetchFn = options.fetchFn ?? fetch;
  const response = await fetchFn(buildGifPageUrl(options, request), {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`KLIPY responded with ${response.status}`);
  }

  const body = (await response.json()) as KlipyPageResponse;

  if (!body.result || !body.data) {
    throw new Error("KLIPY returned an error result");
  }

  // Ads (when enabled in the partner panel) come back in the same list without a `file` — nothing
  // here can render them, so they are dropped rather than crashing the grid.
  const gifs = (body.data.data ?? []).filter(isKlipyGif);

  return {
    gifs,
    page: body.data.current_page ?? request.page,
    hasNext: Boolean(body.data.has_next),
  };
};

/**
 * Tells KLIPY the GIF was actually sent — feeds their ranking. Fire-and-forget: a failure here
 * must never surface to the user, the message is already on its way.
 */
export const trackGifShare = (
  { appKey, customerId, fetchFn }: KlipyClientOptions,
  slug: string,
  query: string,
): void => {
  const doFetch = fetchFn ?? fetch;
  void doFetch(`${KLIPY_API_URL}/${encodeURIComponent(appKey)}/gifs/share/${encodeURIComponent(slug)}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ customer_id: customerId ?? undefined, q: query.trim() || undefined }),
  }).catch(() => undefined);
};

/** Small animated rendition for picker cells. */
export const getGifPreview = (gif: KlipyGif): KlipyFile | null =>
  gif.file.sm?.webp ?? gif.file.sm?.gif ?? gif.file.xs?.webp ?? gif.file.xs?.gif ?? null;

const pickAnimated = (gif: KlipyGif): { file: KlipyFile; format: "webp" | "gif" } | null => {
  const candidates: Array<[KlipyRendition, "webp" | "gif"]> = [
    ["md", "webp"],
    ["hd", "webp"],
    ["md", "gif"],
    ["hd", "gif"],
    ["sm", "webp"],
    ["sm", "gif"],
  ];
  for (const [rendition, format] of candidates) {
    const file = gif.file[rendition]?.[format];
    if (file?.url) {
      return { file, format };
    }
  }
  return null;
};

/**
 * Shapes a KLIPY result as a chat attachment. It is deliberately a plain `files[]` entry with an
 * image content type, so clients that predate the GIF bubble (an OTA-less mobile build, push and
 * channel-list previews) still treat it as an image attachment; the `gif` block is what the
 * updated renderers key on.
 */
export const toGifFileMessageContent = (gif: KlipyGif): FileMessageContent | null => {
  const animated = pickAnimated(gif);
  if (!animated) {
    return null;
  }

  const title = gif.title?.trim() || gif.slug;
  const meta: GifAttachment = {
    provider: "klipy",
    slug: gif.slug,
    width: animated.file.width,
    height: animated.file.height,
    previewUrl: gif.file.sm?.jpg?.url ?? gif.file.md?.jpg?.url ?? null,
    mp4Url: gif.file.md?.mp4?.url ?? gif.file.sm?.mp4?.url ?? null,
  };

  return {
    uid: `klipy-${gif.slug}`,
    originalName: `${title}.${animated.format}`,
    shortName: title,
    size: animated.file.size,
    contentType: `image/${animated.format}`,
    extension: animated.format,
    url: animated.file.url,
    thumbUrl: getGifPreview(gif)?.url ?? null,
    duration: null,
    gif: meta,
  };
};
